/**
 * Compact Country → State/Province → City dataset for the Cascading
 * Dropdown element's "Load Country/State/City" preset.
 *
 * Trade-off: a *complete* worldwide CSC dataset is 25+ MB JSON and
 * impractical to ship in the frontend bundle. This curated subset covers
 * the most commonly-collected countries (~80% of typical form traffic):
 * USA (all 50 states + top cities), Canada (provinces + top cities),
 * United Kingdom (countries + major cities), Australia (states + major
 * cities), and a flat list of the rest of the world's countries with no
 * state/city drill-down. Form owners needing deeper coverage for other
 * countries can paste a custom JSON tree in the element settings.
 *
 * Shape: { [country]: { [state]: [city, …] } }  — empty {} means no
 * states; empty [] means states exist but no cities.
 */

// Hand-picked: top 5–6 cities per US state by population, all 50 states.
// Source: 2024 US Census estimates. Trimmed for bundle size.
const US_STATES = {
  'Alabama':        ['Birmingham', 'Montgomery', 'Huntsville', 'Mobile', 'Tuscaloosa'],
  'Alaska':         ['Anchorage', 'Fairbanks', 'Juneau', 'Wasilla', 'Sitka'],
  'Arizona':        ['Phoenix', 'Tucson', 'Mesa', 'Chandler', 'Scottsdale', 'Glendale'],
  'Arkansas':       ['Little Rock', 'Fort Smith', 'Fayetteville', 'Springdale', 'Jonesboro'],
  'California':     ['Los Angeles', 'San Diego', 'San Jose', 'San Francisco', 'Fresno', 'Sacramento', 'Long Beach', 'Oakland', 'Bakersfield', 'Anaheim'],
  'Colorado':       ['Denver', 'Colorado Springs', 'Aurora', 'Fort Collins', 'Lakewood', 'Boulder'],
  'Connecticut':    ['Bridgeport', 'New Haven', 'Stamford', 'Hartford', 'Waterbury'],
  'Delaware':       ['Wilmington', 'Dover', 'Newark', 'Middletown'],
  'Florida':        ['Jacksonville', 'Miami', 'Tampa', 'Orlando', 'St. Petersburg', 'Hialeah', 'Tallahassee', 'Fort Lauderdale'],
  'Georgia':        ['Atlanta', 'Augusta', 'Columbus', 'Savannah', 'Athens', 'Macon'],
  'Hawaii':         ['Honolulu', 'Hilo', 'Kailua', 'Kapolei', 'Pearl City'],
  'Idaho':          ['Boise', 'Meridian', 'Nampa', 'Idaho Falls', 'Pocatello'],
  'Illinois':       ['Chicago', 'Aurora', 'Naperville', 'Joliet', 'Rockford', 'Springfield', 'Elgin'],
  'Indiana':        ['Indianapolis', 'Fort Wayne', 'Evansville', 'South Bend', 'Carmel', 'Bloomington'],
  'Iowa':           ['Des Moines', 'Cedar Rapids', 'Davenport', 'Sioux City', 'Iowa City', 'Waterloo'],
  'Kansas':         ['Wichita', 'Overland Park', 'Kansas City', 'Olathe', 'Topeka', 'Lawrence'],
  'Kentucky':       ['Louisville', 'Lexington', 'Bowling Green', 'Owensboro', 'Covington'],
  'Louisiana':      ['New Orleans', 'Baton Rouge', 'Shreveport', 'Lafayette', 'Lake Charles'],
  'Maine':          ['Portland', 'Lewiston', 'Bangor', 'South Portland', 'Auburn'],
  'Maryland':       ['Baltimore', 'Frederick', 'Rockville', 'Gaithersburg', 'Bowie', 'Hagerstown', 'Annapolis'],
  'Massachusetts':  ['Boston', 'Worcester', 'Springfield', 'Cambridge', 'Lowell', 'Brockton'],
  'Michigan':       ['Detroit', 'Grand Rapids', 'Warren', 'Sterling Heights', 'Ann Arbor', 'Lansing', 'Flint'],
  'Minnesota':      ['Minneapolis', 'Saint Paul', 'Rochester', 'Duluth', 'Bloomington', 'Plymouth'],
  'Mississippi':    ['Jackson', 'Gulfport', 'Southaven', 'Hattiesburg', 'Biloxi'],
  'Missouri':       ['Kansas City', 'St. Louis', 'Springfield', 'Columbia', 'Independence', 'Lee\'s Summit'],
  'Montana':        ['Billings', 'Missoula', 'Great Falls', 'Bozeman', 'Helena'],
  'Nebraska':       ['Omaha', 'Lincoln', 'Bellevue', 'Grand Island', 'Kearney'],
  'Nevada':         ['Las Vegas', 'Henderson', 'Reno', 'North Las Vegas', 'Sparks', 'Carson City'],
  'New Hampshire':  ['Manchester', 'Nashua', 'Concord', 'Dover', 'Rochester'],
  'New Jersey':     ['Newark', 'Jersey City', 'Paterson', 'Elizabeth', 'Edison', 'Trenton'],
  'New Mexico':     ['Albuquerque', 'Las Cruces', 'Rio Rancho', 'Santa Fe', 'Roswell'],
  'New York':       ['New York City', 'Buffalo', 'Yonkers', 'Rochester', 'Syracuse', 'Albany'],
  'North Carolina': ['Charlotte', 'Raleigh', 'Greensboro', 'Durham', 'Winston-Salem', 'Asheville'],
  'North Dakota':   ['Fargo', 'Bismarck', 'Grand Forks', 'Minot', 'West Fargo'],
  'Ohio':           ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron', 'Dayton'],
  'Oklahoma':       ['Oklahoma City', 'Tulsa', 'Norman', 'Broken Arrow', 'Edmond'],
  'Oregon':         ['Portland', 'Salem', 'Eugene', 'Gresham', 'Hillsboro', 'Bend'],
  'Pennsylvania':   ['Philadelphia', 'Pittsburgh', 'Allentown', 'Erie', 'Reading', 'Harrisburg'],
  'Rhode Island':   ['Providence', 'Warwick', 'Cranston', 'Pawtucket', 'Newport'],
  'South Carolina': ['Charleston', 'Columbia', 'North Charleston', 'Mount Pleasant', 'Greenville'],
  'South Dakota':   ['Sioux Falls', 'Rapid City', 'Aberdeen', 'Brookings', 'Watertown'],
  'Tennessee':      ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga', 'Clarksville'],
  'Texas':          ['Houston', 'San Antonio', 'Dallas', 'Austin', 'Fort Worth', 'El Paso', 'Arlington', 'Plano'],
  'Utah':           ['Salt Lake City', 'West Valley City', 'Provo', 'West Jordan', 'Orem', 'Sandy'],
  'Vermont':        ['Burlington', 'South Burlington', 'Rutland', 'Essex Junction', 'Montpelier'],
  'Virginia':       ['Virginia Beach', 'Norfolk', 'Chesapeake', 'Richmond', 'Newport News', 'Alexandria', 'Arlington'],
  'Washington':     ['Seattle', 'Spokane', 'Tacoma', 'Vancouver', 'Bellevue', 'Kent', 'Everett'],
  'West Virginia':  ['Charleston', 'Huntington', 'Morgantown', 'Parkersburg', 'Wheeling'],
  'Wisconsin':      ['Milwaukee', 'Madison', 'Green Bay', 'Kenosha', 'Racine', 'Appleton'],
  'Wyoming':        ['Cheyenne', 'Casper', 'Laramie', 'Gillette', 'Rock Springs'],
};

const CA_PROVINCES = {
  'Alberta':                  ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'Medicine Hat'],
  'British Columbia':         ['Vancouver', 'Victoria', 'Surrey', 'Burnaby', 'Richmond', 'Kelowna'],
  'Manitoba':                 ['Winnipeg', 'Brandon', 'Steinbach', 'Thompson'],
  'New Brunswick':            ['Moncton', 'Saint John', 'Fredericton', 'Dieppe'],
  'Newfoundland and Labrador':['St. John\'s', 'Mount Pearl', 'Corner Brook', 'Conception Bay South'],
  'Northwest Territories':    ['Yellowknife', 'Hay River', 'Inuvik'],
  'Nova Scotia':              ['Halifax', 'Sydney', 'Dartmouth', 'Truro'],
  'Nunavut':                  ['Iqaluit', 'Rankin Inlet', 'Arviat'],
  'Ontario':                  ['Toronto', 'Ottawa', 'Mississauga', 'Brampton', 'Hamilton', 'London', 'Markham', 'Vaughan'],
  'Prince Edward Island':     ['Charlottetown', 'Summerside', 'Stratford'],
  'Quebec':                   ['Montreal', 'Quebec City', 'Laval', 'Gatineau', 'Longueuil', 'Sherbrooke'],
  'Saskatchewan':             ['Saskatoon', 'Regina', 'Prince Albert', 'Moose Jaw'],
  'Yukon':                    ['Whitehorse', 'Dawson City', 'Watson Lake'],
};

const UK_COUNTRIES = {
  'England':          ['London', 'Birmingham', 'Manchester', 'Liverpool', 'Leeds', 'Sheffield', 'Bristol', 'Newcastle', 'Brighton', 'Cambridge', 'Oxford'],
  'Scotland':         ['Glasgow', 'Edinburgh', 'Aberdeen', 'Dundee', 'Inverness', 'Stirling'],
  'Wales':            ['Cardiff', 'Swansea', 'Newport', 'Wrexham', 'Bangor'],
  'Northern Ireland': ['Belfast', 'Derry', 'Newry', 'Lisburn'],
};

const AU_STATES = {
  'Australian Capital Territory': ['Canberra'],
  'New South Wales':              ['Sydney', 'Newcastle', 'Wollongong', 'Central Coast'],
  'Northern Territory':           ['Darwin', 'Alice Springs', 'Palmerston'],
  'Queensland':                   ['Brisbane', 'Gold Coast', 'Sunshine Coast', 'Townsville', 'Cairns'],
  'South Australia':              ['Adelaide', 'Mount Gambier', 'Whyalla'],
  'Tasmania':                     ['Hobart', 'Launceston', 'Devonport'],
  'Victoria':                     ['Melbourne', 'Geelong', 'Ballarat', 'Bendigo'],
  'Western Australia':            ['Perth', 'Bunbury', 'Geraldton', 'Albany', 'Kalgoorlie'],
};

// Top-25 rest-of-world: states/cities not bundled. Form owner can extend
// via the JSON tree input. Listed alphabetically.
const REST_OF_WORLD = [
  'Argentina', 'Belgium', 'Brazil', 'Chile', 'China', 'Colombia', 'Czech Republic',
  'Denmark', 'Egypt', 'Finland', 'France', 'Germany', 'Greece', 'Hong Kong',
  'India', 'Indonesia', 'Ireland', 'Israel', 'Italy', 'Japan', 'Malaysia',
  'Mexico', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Pakistan',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Romania', 'Russia', 'Saudi Arabia',
  'Singapore', 'South Africa', 'South Korea', 'Spain', 'Sweden', 'Switzerland',
  'Taiwan', 'Thailand', 'Turkey', 'Ukraine', 'United Arab Emirates', 'Vietnam',
];

// Assemble final tree. Levels: Country (key) → State/Province (key) → City (string)
export const COUNTRY_STATE_CITY = {
  'United States':  US_STATES,
  'Canada':         CA_PROVINCES,
  'United Kingdom': UK_COUNTRIES,
  'Australia':      AU_STATES,
  // Rest-of-world countries have no state/city sub-tree by default.
  ...Object.fromEntries(REST_OF_WORLD.map((c) => [c, {}])),
};

// Convenience metadata exposed to the FormBuilder UI.
export const COUNTRY_STATE_CITY_PRESET = {
  id: 'country-state-city',
  label: 'Country → State → City',
  description: 'Pre-loaded with 4 countries that have full state+city drill-down (US, Canada, UK, Australia) plus 46 additional countries as flat options.',
  levels: ['Country', 'State / Province', 'City'],
  tree: COUNTRY_STATE_CITY,
};

// =====================================================================
// Industry → Sub-industry → Specialty
// Curated taxonomy covering the 12 most-common industries collected on
// B2B / lead-gen forms. Form owners can extend via JSON view.
// =====================================================================
const INDUSTRY_TREE = {
  'Technology': {
    'Software':                     ['SaaS', 'Enterprise Software', 'Consumer Apps', 'DevTools', 'AI / ML', 'Cybersecurity'],
    'Hardware':                     ['Semiconductors', 'Consumer Electronics', 'IoT', 'Robotics', 'Networking Equipment'],
    'IT Services':                  ['Managed Services', 'Cloud Consulting', 'Systems Integration', 'IT Staffing'],
    'Telecommunications':           ['Wireless Carriers', 'ISPs', 'Satellite', 'VoIP'],
  },
  'Healthcare': {
    'Providers':                    ['Hospitals', 'Clinics', 'Dental', 'Mental Health', 'Telemedicine', 'Nursing & Long-term Care'],
    'Pharmaceuticals':              ['Brand-name Pharma', 'Generic Pharma', 'OTC', 'Specialty Drugs'],
    'Biotech':                      ['Drug Discovery', 'Genomics', 'Cell & Gene Therapy', 'Diagnostics'],
    'Medical Devices':              ['Imaging', 'Surgical Instruments', 'Wearables', 'Implants', 'In-Vitro Diagnostics'],
    'Health Insurance':             ['HMO', 'PPO', 'Medicare/Medicaid', 'Health Tech Insurance'],
  },
  'Finance': {
    'Banking':                      ['Retail Banking', 'Commercial Banking', 'Investment Banking', 'Private Banking'],
    'Insurance':                    ['Life', 'Property & Casualty', 'Health', 'Reinsurance'],
    'Investment Management':        ['Mutual Funds', 'Hedge Funds', 'Private Equity', 'Venture Capital', 'Wealth Management'],
    'Fintech':                      ['Payments', 'Lending', 'Robo-advisors', 'Neo-banks', 'Crypto Exchanges'],
    'Accounting & Audit':           ['Audit & Assurance', 'Tax Advisory', 'Bookkeeping', 'Forensic Accounting'],
  },
  'Retail & E-commerce': {
    'Brick & Mortar':               ['Apparel', 'Grocery', 'Electronics', 'Home & Garden', 'Specialty'],
    'E-commerce':                   ['DTC Brands', 'Marketplaces', 'Subscription Boxes', 'Resale & Vintage'],
    'Wholesale & Distribution':     ['B2B Wholesale', 'Cash & Carry', 'Drop-shipping'],
  },
  'Manufacturing': {
    'Automotive':                   ['OEMs', 'Tier-1 Suppliers', 'Aftermarket Parts', 'EV Components'],
    'Aerospace & Defense':          ['Commercial Aircraft', 'Military', 'Space & Satellites'],
    'Industrial Machinery':         ['CNC & Tooling', 'Heavy Equipment', 'Robotics & Automation'],
    'Consumer Goods':               ['Appliances', 'Furniture', 'Personal Care'],
    'Chemicals & Materials':        ['Specialty Chemicals', 'Plastics', 'Metals & Mining'],
    'Food & Beverage':              ['Packaged Foods', 'Beverages', 'Confectionery'],
  },
  'Education': {
    'K-12':                         ['Public Schools', 'Private Schools', 'Charter Schools', 'Online K-12'],
    'Higher Education':             ['Universities', 'Community Colleges', 'For-profit Colleges'],
    'EdTech':                       ['LMS Platforms', 'Online Courses & MOOCs', 'Tutoring & Test Prep', 'Language Learning'],
    'Vocational & Continuing':      ['Bootcamps', 'Trade Schools', 'Corporate Training'],
  },
  'Real Estate': {
    'Residential':                  ['Single-family Sales', 'Multi-family Rentals', 'Property Management', 'Vacation Rentals'],
    'Commercial':                   ['Office', 'Retail', 'Industrial & Warehousing', 'Hospitality'],
    'PropTech':                     ['Listings & Search', 'iBuying', 'Lease & Workflow Tools', 'Smart-home Tech'],
    'Construction & Development':   ['Residential GC', 'Commercial GC', 'Civil Engineering', 'Architecture'],
  },
  'Hospitality & Travel': {
    'Lodging':                      ['Hotels', 'Vacation Rentals', 'Resorts', 'Hostels', 'Co-living'],
    'Food & Drink':                 ['Restaurants', 'Cafés & Bakeries', 'Bars & Nightclubs', 'Catering'],
    'Travel & Transport':           ['Airlines', 'Cruise Lines', 'Rail', 'Car Rental', 'OTAs'],
    'Tourism Services':             ['Tour Operators', 'Destination Marketing', 'Experiences & Activities'],
  },
  'Media & Entertainment': {
    'Publishing':                   ['News & Magazines', 'Books', 'Academic & Scholarly', 'Newsletters'],
    'Film, TV & Streaming':         ['Production Studios', 'Distribution', 'Streaming Platforms', 'Post-production'],
    'Music':                        ['Labels', 'Streaming', 'Live Events', 'Publishing & Rights'],
    'Gaming & Esports':             ['AAA Studios', 'Indie Studios', 'Mobile Games', 'Esports Teams'],
    'Advertising & Marketing':      ['Ad Agencies', 'PR Firms', 'AdTech', 'Influencer Marketing'],
  },
  'Legal': {
    'Law Firms':                    ['BigLaw', 'Boutique Firms', 'Solo Practice'],
    'Practice Areas':               ['Corporate', 'Litigation', 'IP', 'Family', 'Criminal', 'Real Estate', 'Tax', 'Immigration'],
    'LegalTech':                    ['Contract Management', 'eDiscovery', 'Practice Management', 'Online Legal Services'],
  },
  'Energy & Utilities': {
    'Oil & Gas':                    ['Upstream', 'Midstream', 'Downstream', 'Oilfield Services'],
    'Renewable Energy':             ['Solar', 'Wind', 'Hydro', 'Geothermal', 'Battery Storage'],
    'Utilities':                    ['Electric Utilities', 'Water', 'Natural Gas Distribution'],
    'Mining & Extraction':          ['Coal', 'Metals & Rare Earths', 'Aggregates'],
  },
  'Government & Non-Profit': {
    'Government':                   ['Federal', 'State', 'Local', 'Defense'],
    'Non-Profit':                   ['Humanitarian', 'Environment', 'Healthcare', 'Arts & Culture', 'Religion'],
    'NGO & International':          ['Development', 'Human Rights', 'Disaster Relief'],
    'Civic Tech':                   ['Open Data', 'GovTech SaaS', 'Election Tech'],
  },
  'Agriculture': {
    'Crops':                        ['Grains', 'Produce', 'Specialty Crops', 'Organic'],
    'Livestock':                    ['Dairy', 'Beef & Poultry', 'Pork', 'Aquaculture'],
    'AgTech':                       ['Precision Agriculture', 'Farm Management Software', 'Drones & Robotics', 'Vertical Farming'],
  },
  'Other':                          {},
};

export const INDUSTRY_PRESET = {
  id: 'industry-sub-specialty',
  label: 'Industry → Sub-industry → Specialty',
  description: '14 top-level industries with 3-level drill-down covering Tech, Healthcare, Finance, Retail, Manufacturing, Education, Real Estate, Hospitality, Media, Legal, Energy, Government, Agriculture, and Other.',
  levels: ['Industry', 'Sub-industry', 'Specialty'],
  tree: INDUSTRY_TREE,
};

// =====================================================================
// Make → Model → Year → Trim
// Top US/global automotive lineups. 4 levels deep = max supported.
// Years cover the last 5 model-years (2021-2025); trims are the most
// commonly-ordered variants per model.
// =====================================================================
const VEHICLE_YEARS = ['2025', '2024', '2023', '2022', '2021'];
const yearTrims = (trims) =>
  Object.fromEntries(VEHICLE_YEARS.map((y) => [y, trims]));

const VEHICLE_TREE = {
  'Toyota': {
    'Camry':          yearTrims(['LE', 'SE', 'XLE', 'XSE', 'TRD']),
    'Corolla':        yearTrims(['L', 'LE', 'SE', 'XLE', 'XSE', 'Hybrid LE']),
    'RAV4':           yearTrims(['LE', 'XLE', 'XLE Premium', 'Adventure', 'TRD Off-Road', 'Limited']),
    'Highlander':     yearTrims(['LE', 'XLE', 'Limited', 'Platinum', 'Hybrid XLE']),
    'Tacoma':         yearTrims(['SR', 'SR5', 'TRD Sport', 'TRD Off-Road', 'TRD Pro', 'Limited']),
    'Tundra':         yearTrims(['SR', 'SR5', 'Limited', 'Platinum', '1794 Edition', 'TRD Pro']),
    'Prius':          yearTrims(['LE', 'XLE', 'Limited']),
  },
  'Honda': {
    'Civic':          yearTrims(['LX', 'Sport', 'EX', 'Touring', 'Si', 'Type R']),
    'Accord':         yearTrims(['LX', 'EX', 'Sport', 'EX-L', 'Touring', 'Hybrid Sport']),
    'CR-V':           yearTrims(['LX', 'EX', 'EX-L', 'Sport', 'Touring', 'Hybrid Sport Touring']),
    'Pilot':          yearTrims(['Sport', 'EX-L', 'Touring', 'Elite', 'Black Edition', 'TrailSport']),
    'Odyssey':        yearTrims(['EX', 'EX-L', 'Touring', 'Elite']),
    'Ridgeline':      yearTrims(['Sport', 'RTL', 'RTL-E', 'Black Edition', 'TrailSport']),
  },
  'Ford': {
    'F-150':          yearTrims(['XL', 'XLT', 'Lariat', 'King Ranch', 'Platinum', 'Limited', 'Raptor']),
    'Mustang':        yearTrims(['EcoBoost', 'GT', 'Mach 1', 'Dark Horse', 'Shelby GT500']),
    'Explorer':       yearTrims(['Base', 'XLT', 'Limited', 'ST', 'Platinum', 'King Ranch']),
    'Escape':         yearTrims(['Base', 'Active', 'ST-Line', 'ST-Line Select', 'Platinum', 'PHEV']),
    'Bronco':         yearTrims(['Base', 'Big Bend', 'Black Diamond', 'Outer Banks', 'Badlands', 'Wildtrak', 'Raptor']),
    'Mach-E':         yearTrims(['Select', 'Premium', 'California Route 1', 'GT', 'GT Performance']),
  },
  'Chevrolet': {
    'Silverado 1500': yearTrims(['WT', 'Custom', 'LT', 'RST', 'LTZ', 'High Country', 'ZR2']),
    'Equinox':        yearTrims(['LS', 'LT', 'RS', 'Premier']),
    'Tahoe':          yearTrims(['LS', 'LT', 'RST', 'Z71', 'Premier', 'High Country']),
    'Suburban':       yearTrims(['LS', 'LT', 'RST', 'Z71', 'Premier', 'High Country']),
    'Camaro':         yearTrims(['LS', 'LT', '1SS', '2SS', 'ZL1']),
    'Bolt EUV':       yearTrims(['LT', 'Premier']),
  },
  'Tesla': {
    'Model 3':        yearTrims(['Rear-Wheel Drive', 'Long Range', 'Performance']),
    'Model Y':        yearTrims(['Rear-Wheel Drive', 'Long Range', 'Performance']),
    'Model S':        yearTrims(['Long Range', 'Plaid']),
    'Model X':        yearTrims(['Long Range', 'Plaid']),
    'Cybertruck':     yearTrims(['Single Motor', 'Dual Motor', 'Cyberbeast (Tri-Motor)']),
  },
  'BMW': {
    '3 Series':       yearTrims(['330i', '330i xDrive', 'M340i', 'M340i xDrive', 'M3', 'M3 Competition']),
    '5 Series':       yearTrims(['530i', '530i xDrive', '540i', 'M550i', 'M5', 'M5 Competition']),
    'X3':             yearTrims(['sDrive30i', 'xDrive30i', 'M40i', 'X3 M', 'X3 M Competition']),
    'X5':             yearTrims(['sDrive40i', 'xDrive40i', 'xDrive50e', 'xDrive50i', 'M60i', 'X5 M Competition']),
    'i4':             yearTrims(['eDrive35', 'eDrive40', 'M50']),
  },
  'Mercedes-Benz': {
    'C-Class':        yearTrims(['C 300', 'C 300 4MATIC', 'AMG C 43', 'AMG C 63 S E Performance']),
    'E-Class':        yearTrims(['E 350', 'E 350 4MATIC', 'E 450 4MATIC', 'AMG E 53', 'AMG E 63 S']),
    'GLE':            yearTrims(['GLE 350', 'GLE 450 4MATIC', 'GLE 580 4MATIC', 'AMG GLE 53', 'AMG GLE 63 S']),
    'GLC':            yearTrims(['GLC 300', 'GLC 300 4MATIC', 'AMG GLC 43', 'AMG GLC 63 S']),
    'S-Class':        yearTrims(['S 500 4MATIC', 'S 580 4MATIC', 'S 580e', 'AMG S 63 E Performance', 'Maybach S 580', 'Maybach S 680']),
  },
  'Audi': {
    'A4':             yearTrims(['Premium', 'Premium Plus', 'Prestige', 'S4']),
    'Q5':             yearTrims(['Premium', 'Premium Plus', 'Prestige', 'SQ5']),
    'Q7':             yearTrims(['Premium', 'Premium Plus', 'Prestige', 'SQ7']),
    'A6':             yearTrims(['Premium', 'Premium Plus', 'Prestige', 'S6', 'RS 6 Avant']),
    'e-tron GT':      yearTrims(['Premium Plus', 'Prestige', 'RS e-tron GT']),
  },
  'Hyundai': {
    'Elantra':        yearTrims(['SE', 'SEL', 'Limited', 'N Line', 'N']),
    'Sonata':         yearTrims(['SE', 'SEL', 'SEL Plus', 'Limited', 'N Line', 'Hybrid Blue']),
    'Tucson':         yearTrims(['SE', 'SEL', 'XRT', 'Limited', 'N Line', 'Hybrid Blue']),
    'Santa Fe':       yearTrims(['SE', 'SEL', 'XRT', 'Limited', 'Calligraphy', 'Hybrid SEL Premium']),
    'IONIQ 5':        yearTrims(['SE Standard Range', 'SE', 'SEL', 'Limited']),
  },
  'Kia': {
    'Forte':          yearTrims(['LX', 'LXS', 'GT-Line', 'GT']),
    'K5':             yearTrims(['LXS', 'GT-Line', 'EX', 'GT']),
    'Sportage':       yearTrims(['LX', 'EX', 'X-Line', 'SX', 'X-Pro Prestige', 'Hybrid SX Prestige']),
    'Telluride':      yearTrims(['LX', 'S', 'EX', 'SX', 'SX Prestige', 'X-Pro']),
    'EV6':            yearTrims(['Light', 'Wind', 'GT-Line', 'GT']),
  },
  'Subaru':           {
    'Outback':        yearTrims(['Base', 'Premium', 'Onyx Edition', 'Limited', 'Touring', 'Wilderness']),
    'Forester':       yearTrims(['Base', 'Premium', 'Sport', 'Limited', 'Touring', 'Wilderness']),
    'Crosstrek':      yearTrims(['Base', 'Premium', 'Sport', 'Limited', 'Wilderness']),
    'Impreza':        yearTrims(['Base', 'Sport', 'RS']),
    'WRX':            yearTrims(['Base', 'Premium', 'Limited', 'GT', 'TR']),
  },
  'Volkswagen':       {
    'Jetta':          yearTrims(['S', 'Sport', 'SE', 'SEL', 'GLI']),
    'Tiguan':         yearTrims(['S', 'SE', 'SE R-Line Black', 'SEL R-Line']),
    'Atlas':          yearTrims(['SE', 'SE w/ Technology', 'Peak Edition', 'SEL', 'SEL Premium R-Line']),
    'ID.4':           yearTrims(['Standard', 'S', 'Pro', 'Pro S', 'Pro S Plus']),
    'Golf GTI':       yearTrims(['S', 'SE', 'Autobahn', '380']),
  },
  'Nissan':           {
    'Altima':         yearTrims(['S', 'SV', 'SR', 'SL']),
    'Sentra':         yearTrims(['S', 'SV', 'SR']),
    'Rogue':          yearTrims(['S', 'SV', 'SL', 'Platinum']),
    'Pathfinder':     yearTrims(['S', 'SV', 'SL', 'Platinum', 'Rock Creek']),
    'Frontier':       yearTrims(['S', 'SV', 'PRO-X', 'PRO-4X', 'SL']),
  },
  'Other Make':       {},
};

export const VEHICLE_PRESET = {
  id: 'vehicle-make-model-year-trim',
  label: 'Make → Model → Year → Trim',
  description: '14 popular makes with 5-6 models each, last 5 model years (2021-2025), and the most commonly ordered trims. Perfect for trade-in, service appointment, and quote-request forms.',
  levels: ['Make', 'Model', 'Year', 'Trim'],
  tree: VEHICLE_TREE,
};

// Registry of all built-in presets. The FormBuilder's "Load preset" menu
// iterates this list so future presets can be added by appending here.
export const CASCADING_PRESETS = [
  COUNTRY_STATE_CITY_PRESET,
  INDUSTRY_PRESET,
  VEHICLE_PRESET,
];
